// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
// Custom components
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../auth-context/auth.context';
import AuthApi from '../../../api/auth';
import DefaultAuth from 'layouts/auth/Default';
// Assets
import illustration from 'assets/img/auth/auth.png';
import { MdOutlineRemoveRedEye, MdCheckCircle, MdRadioButtonUnchecked } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';

function SetPassword() {
  const [password, setPassword] = useState('');
  const [confirmpassword, setConfirmPassword] = useState('');
  const [buttonText, setButtonText] = useState('Set password');
  const [error, setError] = useState(undefined);
  const history = useHistory();
  const [show, setShow] = useState(false);

  // Password requirements state
  const [requirements, setRequirements] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
  });

  const [isStrong, setIsStrong] = useState(false);

  useEffect(() => {
    const reqs = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    setRequirements(reqs);
    setIsStrong(Object.values(reqs).every(Boolean));
  }, [password]);

  // Chakra color mode
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorBrand = useColorModeValue('brand.500', 'white');
  const brandStars = useColorModeValue('brand.500', 'brand.400');

  const handleClick = () => setShow(!show);

  const register = async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (!password || !confirmpassword) {
      return setError('You must enter password.');
    }
    if (password !== confirmpassword) {
      return setError('Passwords do not match');
    }
    if (!isStrong) {
      return setError('Password does not meet all requirements.');
    }

    try {
      const token = new URLSearchParams(window.location.search).get('token');
      let response = await AuthApi.SetPassword(password, token);
      response = response.data;
      if (response.status === 401) {
        return setError('Unauthorized Path');
      }
      if (response.status_code !== 1 && response.error) {
        return setError(response.message);
      }
      return history.push('/auth/sign-in');
    } catch (err) {
      console.log(err);
      if (err.response) {
        const message = err.response.data.message;
        if (Array.isArray(message)) {
          return setError(message.join('. '));
        }
        return setError(message);
      }
      return setError('There has been an error.');
    }
  };

  const RequirementItem = ({ isMet, label }) => (
    <Flex align="center" mb="4px">
      <Icon
        as={isMet ? MdCheckCircle : MdRadioButtonUnchecked}
        color={isMet ? 'green.500' : 'gray.400'}
        me="8px"
      />
      <Text fontSize="xs" color={isMet ? 'green.500' : 'gray.500'}>
        {label}
      </Text>
    </Flex>
  );

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w='100%'
        mx={{ base: 'auto', lg: '0px' }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection='column'
      >
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Set Password
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'
          >
            Reset your account's password!
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: '100%', md: '420px' }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: 'auto', lg: 'unset' }}
          me='auto'
          mb={{ base: '20px', md: 'auto' }}
        >
          <Flex
            zIndex='2'
            direction='column'
            w={{ base: '100%', md: '420px' }}
            maxW='100%'
            background='transparent'
            borderRadius='15px'
            mx={{ base: 'auto', lg: 'unset' }}
            me='auto'
            mb={{ base: '20px', md: 'auto' }}
          >
            <h4
              style={{
                fontSize: '.9em',
                color: 'red',
                textAlign: 'center',
                fontWeight: 400,
                transition: '.2s all',
                marginBottom: '10px'
              }}
            >
              {error}
            </h4>
            <FormControl>
              <FormLabel
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                display='flex'
              >
                Password<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size='md'>
                <Input
                  variant='auth'
                  isRequired={true}
                  fontSize='sm'
                  placeholder='Min. 8 characters'
                  mb='12px'
                  size='lg'
                  type={show ? 'text' : 'password'}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setError(undefined);
                  }}
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>

              <Box mb="24px" ml="4px">
                <Text fontSize="xs" fontWeight="700" mb="8px" color={textColor}>
                  Password Requirements:
                </Text>
                <Stack spacing={0}>
                  <RequirementItem isMet={requirements.length} label="At least 8 characters" />
                  <RequirementItem isMet={requirements.uppercase} label="At least one uppercase letter" />
                  <RequirementItem isMet={requirements.lowercase} label="At least one lowercase letter" />
                  <RequirementItem isMet={requirements.number} label="At least one number" />
                  <RequirementItem isMet={requirements.special} label="At least one symbol (!@#$%^&*)" />
                </Stack>
              </Box>

              <FormLabel
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                display='flex'
              >
                Confirm Password <Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size='md'>
                <Input
                  variant='auth'
                  isRequired={true}
                  fontSize='sm'
                  placeholder='********'
                  mb='24px'
                  size='lg'
                  type={show ? 'text' : 'password'}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                    setError(undefined);
                  }}
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              <Button
                fontSize='sm'
                variant='brand'
                fontWeight='500'
                w='100%'
                h='50'
                mb='24px'
                onClick={register}
                isDisabled={!isStrong || !confirmpassword}
              >
                {buttonText}
              </Button>
            </FormControl>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SetPassword;
